import React from "react";
import {
  Card,
  CardContent,
  Typography,
  useTheme,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import CardHeader from "./CardHeader.jsx";
import { useRoles } from "../../../context/AuthenticationTokenContext";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import * as f from "../../../utils/formatter.js";
import { ContactCard } from "./ContactCard.jsx";
import { MoreInfoCard } from "./MoreInfoCard.jsx";
import { CopyIcon } from "../../../components/common/Copy.jsx";

/**
 * @typedef {Object} Client
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} email
 */

/**
 * @typedef {Object} ClientInfoCardProps
 * @property {Client} client
 * @property {boolean} displayMinimalInfo
 * @property {Object} lastAppointment
 */

/**
 * @param {ClientInfoCardProps} props
 */
const ClientInfoCard = ({
  client,
  displayMinimalInfo = false,
  lastAppointment,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activeTab, setActiveTab] = React.useState("info");

  const roles = useRoles();
  const canSeeBirthInfo = isGrantedAny(
    roles,
    new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );
  const canSeeClientInsuranceCompany = isGrantedAny(
    roles,
    new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const canSeeClientClinic = isGrantedAny(
    roles,
    new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );

  const canSeeContactCard = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );
  return (
    <>
      <Card>
        <CardContent>
          <CardHeader
            title={t("client.info.card.title")}
            color={theme.palette.info.main}
            faIcon={faUser}
          />
          <Tabs value={activeTab} onChange={(_, tab) => setActiveTab(tab)}>
            <Tab label="Info o klientovi" value="info" />
            <Tab label="Další info" value="more" />
          </Tabs>
          <TabContext value={activeTab}>
            <TabPanel value="info">
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: "repeat(5, minmax(max-content, 1fr))",
                }}
              >
                <Typography variant="body1">
                  <strong>ID klienta</strong> <br />
                  {f.value(client.labs_id)}
                  <CopyIcon text={f.value(client.labs_id)} />
                </Typography>

                <Typography variant="body1">
                  <strong>{t("client.info.fullName")}</strong> <br />
                  {f.fullName(client)}
                  <CopyIcon text={f.fullName(client)} />
                </Typography>

                {!displayMinimalInfo && canSeeBirthInfo && (
                  <Typography variant="body1">
                    <strong>{t("client.info.birthNumber")}</strong> <br />
                    {client.birth_number}
                    <CopyIcon text={f.value(client.birth_number)} />
                  </Typography>
                )}

                {!displayMinimalInfo && canSeeBirthInfo && (
                  <Typography variant="body1">
                    <strong>{t("client.info.birthDate")}</strong>
                    <br />
                    {f.date(client.birthdate)}
                  </Typography>
                )}

                {!displayMinimalInfo && (
                  <Typography variant="body1">
                    <strong>{t("client.info.age")}</strong>
                    <br />
                    {f.ageFromBirthDate(client.birthdate)}
                  </Typography>
                )}

                {canSeeClientClinic && (
                  <Typography variant="body1">
                    <strong>Klinika</strong> <br />
                    {f.value(client?.location?.name)}
                  </Typography>
                )}

                {!displayMinimalInfo && (
                  <Typography variant="body1">
                    <strong>{t("entity.client.sex")}</strong> <br />
                    {client.sex != null
                      ? t(`sex.${client.sex}`)
                      : f.value(null)}
                  </Typography>
                )}

                {!displayMinimalInfo && (
                  <Typography variant="body1">
                    <strong>Jazyk klienta</strong> <br />
                    {f.value(client?.prefs?.language)}
                  </Typography>
                )}

                {!displayMinimalInfo && (
                  <Typography variant="body1">
                    <strong>Další jazyky</strong> <br />
                    {f.value(
                      client?.languageInfoTags?.map((t) => t.value).join(", "),
                    )}
                  </Typography>
                )}
              </Box>

              {/* Contact Information */}
              {canSeeContactCard && (
                <Box sx={{ mt: 2 }}>
                  <ContactCard client={client} />
                </Box>
              )}
            </TabPanel>
            <TabPanel value="more">
              <MoreInfoCard
                client={client}
                lastAppointment={lastAppointment}
                canSeeClientInsuranceCompany={canSeeClientInsuranceCompany}
              />
            </TabPanel>
          </TabContext>
        </CardContent>
      </Card>
    </>
  );
};

export default ClientInfoCard;
